import clsx from "clsx";

import { useMoneySaved } from "./MoneySavedContext";
import { SlotMachineCurrency } from "./SlotMachineCurrency";
import { useScrollReveal } from "./useScrollReveal";
import { formatCurrency } from "./utils";

function RevealElement({
  children,
  isVisible,
  delay,
  className,
}: {
  children: React.ReactNode;
  isVisible: boolean;
  delay: number;
  className?: string;
}) {
  return (
    <div
      className={clsx(
        "transition-all duration-1000",
        isVisible ? "translate-y-0 opacity-100" : "translate-y-10 opacity-0",
        className,
      )}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </div>
  );
}

export function EstimatedSavingsBand() {
  const { totalSavings, totalEstimatedSavings, totalSpend } = useMoneySaved();
  const { ref, isVisible } = useScrollReveal();

  if (totalEstimatedSavings <= 0) return null;

  const estimatedExceedsProven = totalEstimatedSavings > totalSavings;

  return (
    <section
      ref={ref}
      className="scroll-section relative bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900"
    >
      {/* Ambient glow */}
      <div className="pointer-events-none absolute top-1/3 left-1/4 h-64 w-64 rounded-full bg-indigo-500/10 blur-3xl" />
      <div className="pointer-events-none absolute right-1/4 bottom-1/4 h-48 w-48 rounded-full bg-purple-500/10 blur-3xl" />

      <div className="relative z-10 w-full max-w-[960px] px-4 text-center">
        {estimatedExceedsProven ? (
          <>
            <RevealElement isVisible={isVisible} delay={0}>
              <p className="text-xs font-semibold tracking-[0.2em] text-indigo-400 uppercase">
                The Bigger Picture
              </p>
            </RevealElement>

            <RevealElement isVisible={isVisible} delay={200}>
              <h2 className="mx-auto mt-6 max-w-lg text-3xl leading-tight font-extrabold text-white md:text-5xl">
                And that&rsquo;s just what we can prove.
              </h2>
            </RevealElement>

            <RevealElement isVisible={isVisible} delay={400}>
              <p
                className="mt-8 bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text py-2 leading-none font-extrabold tracking-tight text-transparent"
                style={{ fontSize: "clamp(2rem, 8vw, 4.5rem)" }}
              >
                <SlotMachineCurrency
                  value={totalEstimatedSavings}
                  trigger={isVisible}
                  formatFn={formatCurrency}
                />
              </p>
              <p className="mt-2 text-sm font-semibold tracking-wide text-indigo-400/70 uppercase">
                Estimated Total Savings
              </p>
            </RevealElement>

            <RevealElement isVisible={isVisible} delay={600}>
              <p className="mx-auto mt-8 max-w-lg text-lg leading-relaxed text-slate-400">
                Of the{" "}
                <strong className="text-slate-300">
                  {formatCurrency(totalSpend)}
                </strong>{" "}
                you spent on ads, Freshpaint saved you{" "}
                <strong className="text-slate-300">
                  {formatCurrency(totalSavings)}
                </strong>
                . Industry benchmarks suggest your total impact is even bigger.
              </p>
            </RevealElement>
          </>
        ) : (
          <>
            <RevealElement isVisible={isVisible} delay={0}>
              <p className="text-xs font-semibold tracking-[0.2em] text-indigo-400 uppercase">
                Beating the Benchmark
              </p>
            </RevealElement>

            <RevealElement isVisible={isVisible} delay={200}>
              <h2 className="mx-auto mt-6 max-w-lg text-3xl leading-tight font-extrabold text-white md:text-5xl">
                You&rsquo;re outperforming expectations.
              </h2>
            </RevealElement>

            <RevealElement isVisible={isVisible} delay={400}>
              <div className="mt-8 flex flex-wrap items-center justify-center gap-4 md:gap-6">
                <div className="text-center">
                  <p
                    className="py-2 leading-none font-extrabold text-slate-500"
                    style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)" }}
                  >
                    <SlotMachineCurrency
                      value={totalEstimatedSavings}
                      trigger={isVisible}
                      formatFn={formatCurrency}
                    />
                  </p>
                  <p className="mt-2 text-sm text-slate-500">
                    Benchmark estimate
                  </p>
                </div>
                <span className="text-2xl text-slate-600">&rarr;</span>
                <div className="text-center">
                  <p
                    className="bg-gradient-to-r from-indigo-400 via-purple-400 to-pink-400 bg-clip-text py-2 leading-none font-extrabold text-transparent"
                    style={{ fontSize: "clamp(1.5rem, 5vw, 3rem)" }}
                  >
                    <SlotMachineCurrency
                      value={totalSavings}
                      trigger={isVisible}
                      formatFn={formatCurrency}
                    />
                  </p>
                  <p className="mt-2 text-sm text-slate-400">Actual savings</p>
                </div>
              </div>
            </RevealElement>

            <RevealElement isVisible={isVisible} delay={600}>
              <p className="mx-auto mt-8 max-w-lg text-lg leading-relaxed text-slate-400">
                The industry benchmark estimates 30% of ad spend as typical
                savings. Your CAPI integration and event optimization are
                delivering outsized results.
              </p>
            </RevealElement>
          </>
        )}
      </div>
    </section>
  );
}
